// @ts-check

const _ = require('lodash')
const { AsyncRay } = require('async-ray')

const { generateRegExpFromString, splitRegExpStringToArray } = require('./regexp')
const { isUUID } = require('../validators')

class Conditions {
  static get validatorRules() {
    return {
      type: 'one-of',
      rules: this.tagsOnly.map(tag => {
        return {
          type: 'object',
          props: {
            tag: {
              type: 'string',
              enum: [ this.tagsMeta[tag].tag, this.tagsMeta[tag].path ]
            },
            value: this.tagsMeta[tag].valueValidator
          }
        }
      })
    }
  }

  static get tagsMeta() {
    return {
      text: {
        tag: 'text',
        path: 'payload.text',
        valueValidator: {
          type: 'string'
        }
      },
      sender: {
        tag: 'sender',
        path: 'payload.sender',
        valueValidator: {
          type: 'array',
          items: {
            type: 'string'
          }
        }
      },
      recipient: {
        tag: 'recipient',
        path: 'payload.recipient',
        valueValidator: {
          type: 'string'
        }
      },
      group: {
        tag: 'group',
        path: 'meta._recipientData.group',
        valueValidator: {
          type: 'array',
          items: {
            type: 'string'
          }
        }
      },
      mccmnc: {
        tag: 'mccmnc',
        path: 'meta._recipientData',
        valueValidator: {
          type: 'custom',
          messages: {
            // Register our new error message text
            notObject: 'mccmnc must be a plain object!',
            invalidMCC: 'mcc must be a three-digit value! Actual: {actual}',
            invalidMNC: 'mnc must be a string \'all\' or an array of 2-4 digit strings!'
          },
          check(value) {
            if (typeof value !== 'object' || !value) {
              return this.makeError('notObject')
            }

            for (const mcc in value) {
              if (!/^\d{3}$/.test(mcc)) {
                return this.makeError('invalidMCC', 'three-digit', mcc)
              }

              const mnc = value[mcc]
              if (mnc !== 'all' && (!Array.isArray(mnc) || mnc.some(v => typeof v !== 'string' || !/^\d{2,4}$/.test(v)))) {
                return this.makeError('invalidMNC', 'all or 2-4 digits', mnc)
              }
            }

            return true
          }
        }
      },
      contactList: {
        tag: 'contactList',
        path: 'payload.recipient',
        valueValidator: {
          type: 'array',
          items: {
            type: 'uuid'
          }
        }
      },
      keywords: {
        tag: 'keywords',
        path: 'payload.text',
        valueValidator: {
          type: 'array',
          items: {
            type: 'uuid'
          }
        }
      },
      type: {
        tag: 'type',
        path: 'type',
        valueValidator: {
          type: 'array',
          items: {
            type: 'string'
          }
        }
      },

      // TODO: Удалить когда поменяем все пути в базе.
      get 'payload.text'() {
        return this.text
      },
      get 'payload.type'() {
        return this.type
      },
      get 'payload.sender'() {
        return this.sender
      },
      get 'payload.recipient'() {
        return this.recipient
      },
      get 'meta._recipientData.group'() {
        return this.group
      },
      get 'meta._recipientData'() {
        return this.mccmnc
      }
    }
  }

  static get tagsOnly() {
    return [ 'text', 'sender', 'recipient', 'group', 'mccmnc', 'contactList', 'type', 'keywords' ]
  }

  static get generateRegExpFromString() {
    return generateRegExpFromString
  }

  /**
   * Функция сравнения одного правила
   * В ответе может быть как boolean так и Promise<boolean>,
   * в зависимости от реализации метода checkInContactLists
   * По умолчанию отрабатывает синхронно
   *
   * @param {object} condition
   * @param {string} [condition.path]
   * @param {string} [condition.type]
   * @param {string} [condition.tag]
   * @param {boolean} [condition.negate]
   * @param {string|string[]|object} [condition.value]
   * @param {object} data
   *
   * @returns {boolean|Promise<boolean>}
   */
  static check(condition, data) {
    let result = false

    condition = this.standardizeCondition(condition)

    const { tag, value } = condition

    const tagMeta = this.getTagMeta(tag)
    const targetValue = _.get(data, tagMeta.path)

    switch (tag) {
      case this.tagsMeta.mccmnc.tag: {
        let isValid = false
        for (const mcc in value) {
          if (mcc === targetValue.mcc) {
            if (value[mcc] === 'all') {
              isValid = true
              break
            }
            for (const mnc of value[mcc]) {
              if (targetValue.mnc === mnc) {
                isValid = true
                break
              }
            }
            break
          }
        }
        result = isValid
        break
      }

      case this.tagsMeta.type.tag:
      case this.tagsMeta.group.tag:
      case this.tagsMeta.sender.tag: {
        result = !value.length || value.some(v => this.compareStrings(targetValue, v))
        break
      }

      case this.tagsMeta.contactList.tag: {
        result = !value.length || this.checkInContactLists(targetValue, value)
        break
      }

      case this.tagsMeta.keywords.tag: {
        result = !value.length || this.checkInKeywordLists(targetValue, value)
        break
      }

      case this.tagsMeta.text.tag:
      case this.tagsMeta.recipient.tag:
      default: {
        result = this.compareStrings(targetValue, value)
        break
      }
    }

    return result
  }

  static filterConditionsArray(conditionsArray = []) {
    const result = []

    if (Array.isArray(conditionsArray) && conditionsArray.length) {
      for (const condition of conditionsArray) {
        if (_.isPlainObject(condition)) {
          const { path, type, tag } = condition
          const convertedPath = Conditions.getTagMeta(path || type || tag)

          if (result.find(({ tag }) => convertedPath.tag === tag)) {
            continue
          }

          let { value } = condition

          switch (convertedPath.tag) {
            case 'sender':
            case 'group':
              if (typeof value === 'string') {
                value = splitRegExpStringToArray(value)
              }
              break
          }

          if (convertedPath && Conditions.validateValue(value, convertedPath.tag)) {
            result.push({
              tag: convertedPath.tag,
              value
            })
          }
        }
      }
    }

    return result
  }

  static validateValue(value, tag) {
    let result = false

    switch (tag) {
      case this.tagsMeta.text.tag:
      case this.tagsMeta.recipient.tag: {
        if (typeof value === 'string' && value) {
          result = true
        }
        break
      }
      case this.tagsMeta.type.tag:
      case this.tagsMeta.group.tag:
      case this.tagsMeta.sender.tag: {
        if (Array.isArray(value) && value.length) {
          result = true
        }
        break
      }
      case this.tagsMeta.mccmnc.tag: {
        if (_.isPlainObject(value) && Object.keys(value).length) {
          result = true
        }
        break
      }
      case this.tagsMeta.contactList.tag: {
        if (Array.isArray(value) && value.length && value.every(isUUID)) {
          result = true
        }
        break
      }
      case this.tagsMeta.keywords.tag: {
        if (Array.isArray(value) && value.length && value.every(isUUID)) {
          result = true
        }
        break
      }
    }

    return result
  }

  // eslint-disable-next-line no-unused-vars
  static checkInContactLists(recipient, ListIds = []) {
    return false
  }

  // eslint-disable-next-line no-unused-vars
  static checkInKeywordLists(text, ListIds = []) {
    return false
  }

  static getTagMeta(path) {
    if (!this.tagsMeta[path]) {
      throw new Error(`Path '${path}' not supported.`)
    }

    return this.tagsMeta[path]
  }

  static standardizeCondition(originalCondition) {
    const { path, type, tag, value } = originalCondition

    const tagMeta = this.getTagMeta(path || type || tag)

    const standardCondition = {
      tag: tagMeta.tag,
      value
    }

    switch (standardCondition.tag) {
      case this.tagsMeta.type.tag:
      case this.tagsMeta.group.tag:
      case this.tagsMeta.sender.tag:
        if (typeof value === 'string') {
          if (value.indexOf('^(') === 0 && value.lastIndexOf(')$') === value.length - 2) {
            standardCondition.value = splitRegExpStringToArray(value)
          } else {
            standardCondition.value = [ value ]
          }
        }

        break
    }

    return standardCondition
  }

  static compareStrings(compareThis, compareWith) {
    let result = false

    compareThis = _.toString(compareThis)
    compareWith = _.toString(compareWith)

    const regex = this.generateRegExpFromString(compareWith)
    if (regex) {
      result = regex.test(compareThis)
    }

    return result
  }

  /**
   * Проверяет массив условий сразу
   * Метод всегда асинхронный, работает с async-ray
   *
   * @param {object} testingValue
   * @param {object[]} conditions
   * @returns {Promise<boolean>}
   */
  static async asyncCheckConditions(testingValue = {}, conditions = []) {
    const uniqueConditions = conditions.filter((rule, index, rules) => {
      return rules.findIndex(({ tag }) => rule.tag === tag) === index
    })

    return await AsyncRay(uniqueConditions).aEvery(async rule => await this.check(rule, testingValue))
  }
}

module.exports = Conditions
